// JOBE-0044.14: load/error variant router for the Library list area.
//
// Wraps the existing `<LibraryTable>` (JOBE-0044.4) and routes through
// `pickVariant({…})` to render the appropriate state component. The
// header row + chip row are owned by `LibraryListPage`; this component
// owns the table-body region (and the unreachable banner above it).
//
// Reads `EntityTypesContext` (JOBE-0044.6) so the Cedar-denied detect
// has its tri-state input. Reads `lastNonEmptyPage` from the parent so
// `dropSuggestion` has a basis for its filter-empty heuristic.

import { useContext } from 'react';
import { LibraryTable } from '../LibraryTable.jsx';
import { EntityTypesContext } from '../EntityTypesContext.js';
import { pickVariant } from './pickVariant.js';
import { SkeletonState } from './SkeletonState.jsx';
import { EmptyState } from './EmptyState.jsx';
import { FilterEmptyState, FilterEmptyMeta } from './FilterEmptyState.jsx';
import { UnreachableState } from './UnreachableState.jsx';
import { CedarDeniedState } from './CedarDeniedState.jsx';
import { HardErrorState } from './HardErrorState.jsx';

function countRemovableChips(chips) {
  if (!Array.isArray(chips)) return 0;
  return chips.filter((c) => c && c.kind !== 'scope').length;
}

export function LibraryListBody({
  rows,
  loadState,
  error,
  nextCursor,
  onLoadMore,
  selectedUrn,
  onSelect,
  onChipAppend,
  onRetry,
  chips,
  lastNonEmptyPage,
  lastNonEmptyPageAt,
  onRemoveChip,
}) {
  const entityTypesCtx = useContext(EntityTypesContext);
  const removableChips = countRemovableChips(chips);
  const cacheHit = Array.isArray(lastNonEmptyPage) && lastNonEmptyPage.length > 0;

  const variant = pickVariant({
    loadState,
    items: rows,
    removableChips,
    cacheHit,
    entityTypesCtx,
  });

  // Static header — kept identical to LibraryTable's header so the
  // scaffold renders normally per spec §Variant 1.
  const tableHeader = (
    <header className="library-table-header" role="row">
      <span className="library-row-star" aria-hidden="true" />
      <span className="library-table-col library-table-col-title">Title</span>
      <span className="library-table-col library-table-col-kind">Kind</span>
      <span className="library-table-col library-table-col-owner">Owner</span>
      <span className="library-table-col library-table-col-where">Where</span>
      <span className="library-table-col library-table-col-updated">Updated</span>
    </header>
  );

  if (variant === 'ready') {
    // Happy path — defer to the full LibraryTable.
    return (
      <LibraryTable
        rows={rows}
        loadState={loadState}
        error={error}
        nextCursor={nextCursor}
        onLoadMore={onLoadMore}
        selectedUrn={selectedUrn}
        onSelect={onSelect}
        onChipAppend={onChipAppend}
        onRetry={onRetry}
      />
    );
  }

  if (variant === 'skeleton') {
    return (
      <section
        className="library-table"
        aria-label="Library results"
        data-variant="skeleton"
      >
        {tableHeader}
        <SkeletonState rows={8} />
      </section>
    );
  }

  if (variant === 'empty') {
    return (
      <section
        className="library-table"
        aria-label="Library results"
        data-variant="empty"
      >
        {tableHeader}
        <EmptyState />
      </section>
    );
  }

  if (variant === 'filter-empty') {
    return (
      <section
        className="library-table"
        aria-label="Library results"
        data-variant="filter-empty"
      >
        <div className="library-result-meta-strip">
          <FilterEmptyMeta />
        </div>
        {tableHeader}
        <FilterEmptyState
          activeChips={chips}
          lastNonEmptyPage={lastNonEmptyPage}
          onRemoveChip={onRemoveChip}
        />
      </section>
    );
  }

  if (variant === 'cedar-denied') {
    return (
      <section
        className="library-table"
        aria-label="Library results"
        data-variant="cedar-denied"
      >
        {tableHeader}
        <CedarDeniedState />
      </section>
    );
  }

  if (variant === 'unreachable') {
    return (
      <section
        className="library-table"
        aria-label="Library results"
        data-variant="unreachable"
      >
        {tableHeader}
        <UnreachableState
          cachedRows={lastNonEmptyPage || []}
          cachedAt={lastNonEmptyPageAt}
          selectedUrn={selectedUrn}
          onSelect={onSelect}
          onChipAppend={onChipAppend}
          onRetry={onRetry}
        />
      </section>
    );
  }

  // error — hard error panel.
  return (
    <section
      className="library-table"
      aria-label="Library results"
      data-variant="error"
    >
      {tableHeader}
      <HardErrorState error={error} onRetry={onRetry} />
    </section>
  );
}
